import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  ArrowLeft,
  Loader2,
  Wallet,
  Download,
  FileDown,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import {
  buildAgencyLedgerCSV,
  downloadCSV,
  downloadLedgerPDF,
  AggregatedLedgerTable,
  DateRangeChips,
} from "@/pages/MyLedger";

const rupee = (n) =>
  "₹" +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function _istISODate(offsetDays = 0) {
  const now = new Date();
  const ist = new Date(now.getTime() + (330 - now.getTimezoneOffset()) * 60000);
  ist.setDate(ist.getDate() - offsetDays);
  return ist.toISOString().slice(0, 10);
}

// -----------------------------------------------------------------------------
// Agency-member self-serve ledger. Resolves the user's primary group on the
// backend — the page never asks for a group_id, so members can only see
// their own agency.
// -----------------------------------------------------------------------------
export default function AgencyLedger() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [range, setRange] = useState({ from: "2020-01-01", to: _istISODate(0) });
  const [sortOrder, setSortOrder] = useState("desc");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(
        `/wallet/ledger/agency/me?from_=${range.from}&to=${range.to}`
      );
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  const agencyName = data?.agency?.name || "";

  return (
    <div className="min-h-screen bg-[#F8F8F3]">
      <header className="border-b border-[#EDEDE5] bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/home")}
              className="w-9 h-9 rounded flex items-center justify-center hover:bg-[#F0F0EA]"
              data-testid="agency-ledger-back"
              title="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <Wallet className="w-5 h-5 text-[#385C4B]" />
            <div>
              <h1
                className="font-semibold text-lg tracking-tight text-[#1C1C19] leading-tight"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                My Agency Ledger
              </h1>
              {agencyName && (
                <div className="text-[11px] text-[#6B6B61] font-mono flex items-center gap-1.5 mt-0.5">
                  <Building2 className="w-3 h-3" />
                  {agencyName}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-medium text-[#1C1C19]">
                {user?.display_name}
              </div>
              <div className="text-[10px] text-[#6B6B61] font-mono">
                Agency Member
              </div>
            </div>
            <button
              onClick={logout}
              className="text-xs px-2.5 py-1.5 rounded border border-[#DCDCD3] hover:bg-[#F0F0EA]"
              data-testid="agency-ledger-logout"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4">
        {loading && (
          <div className="flex items-center justify-center py-16" data-testid="agency-ledger-loading">
            <Loader2 className="w-6 h-6 animate-spin text-[#385C4B]" />
          </div>
        )}

        {!loading && error && (
          <div
            className="bg-red-50 border border-red-200 text-red-800 rounded p-4 text-sm"
            data-testid="agency-ledger-error"
          >
            <b>Unable to load ledger.</b>
            <div className="text-xs mt-1">{error}</div>
            <button
              onClick={load}
              className="mt-3 text-xs px-3 py-1.5 border border-red-300 rounded hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && data && (
          <>
            <DateRangeChips
              from={range.from}
              to={range.to}
              onChange={setRange}
              testidPrefix="agency-ledger-date"
            />
            {/* Totals cards */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm mb-4">
              {[
                ["Opening", data.totals.opening],
                ["Deliveries", data.totals.delivery_earnings],
                ["Adjustments", data.totals.adjustments],
                // Iter74b: negate on-screen Paid to match admin Wallet + PDF
                // convention. Screen convention: money leaving the ledger
                // shows negative. Prior version displayed positive here,
                // creating a mismatch with the downloaded PDF.
                ["Paid", -(data.totals.payments_received || 0)],
                ["Balance", data.totals.balance],
              ].map(([label, val]) => (
                <div
                  key={label}
                  className="bg-white border border-[#EDEDE5] rounded px-3 py-2"
                  data-testid={`agency-ledger-total-${label.toLowerCase()}`}
                >
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B61]">
                    {label}
                  </div>
                  <div
                    className={`font-mono font-semibold text-base ${
                      label === "Balance"
                        ? val > 0
                          ? "text-red-700"
                          : val < 0
                          ? "text-emerald-700"
                          : ""
                        : ""
                    }`}
                  >
                    {rupee(val)}
                  </div>
                </div>
              ))}
            </div>

            {/* Download bar */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() =>
                  setSortOrder((v) => (v === "desc" ? "asc" : "desc"))
                }
                data-testid="agency-ledger-sort-toggle"
                title={
                  sortOrder === "desc"
                    ? "Abhi Newest first — click for bank passbook mode"
                    : "Abhi Oldest first (passbook) — click for Newest first"
                }
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#385C4B]/40 text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white transition-colors"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6L4 10M4 10L8 14M4 10H16M16 18L20 14M20 14L16 10M20 14H8" /></svg>
                Sort: {sortOrder === "desc" ? "Newest first" : "Oldest first"}
              </button>
              <button
                onClick={async (ev) => {
                  // Iter74: fresh-fetch before CSV so downloaded file
                  // always matches the currently-visible range/totals.
                  const btn = ev.currentTarget;
                  btn.setAttribute("data-loading", "1");
                  try {
                    const { data: fresh } = await api.get(
                      `/wallet/ledger/agency/me?from_=${range.from}&to=${range.to}`
                    );
                    setData(fresh);
                    downloadCSV(
                      `${(fresh.agency?.name || agencyName).replace(/\s+/g, "_")}_Ledger_${fresh.to_date}.csv`,
                      buildAgencyLedgerCSV(fresh, sortOrder)
                    );
                  } catch (e) {
                    toast.error(formatApiError(e) || "CSV download failed");
                  } finally {
                    btn.removeAttribute("data-loading");
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#385C4B]/40 text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white transition-colors data-[loading=1]:opacity-60"
                data-testid="agency-ledger-download-csv"
              >
                <Download className="w-3.5 h-3.5" /> Download Excel (CSV)
              </button>
              <button
                onClick={async (ev) => {
                  // Iter74: fresh-fetch before PDF (same rationale as CSV).
                  const btn = ev.currentTarget;
                  btn.setAttribute("data-loading", "1");
                  try {
                    const { data: fresh } = await api.get(
                      `/wallet/ledger/agency/me?from_=${range.from}&to=${range.to}`
                    );
                    setData(fresh);
                    const name = fresh.agency?.name || agencyName;
                    downloadLedgerPDF({
                      title: `Agency Ledger — ${name}`,
                      subtitle: `Window ${fresh.from_date} to ${fresh.to_date} · Generated ${new Date().toLocaleDateString("en-IN")}`,
                      ledger: fresh,
                      isAgency: true,
                      filename: `${name.replace(/\s+/g, "_")}_Ledger_${fresh.to_date}.pdf`,
                      sortOrder,
                    });
                  } catch (e) {
                    toast.error(formatApiError(e) || "PDF download failed");
                  } finally {
                    btn.removeAttribute("data-loading");
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#385C4B]/40 text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white transition-colors data-[loading=1]:opacity-60"
                data-testid="agency-ledger-download-pdf"
              >
                <FileDown className="w-3.5 h-3.5" /> Download PDF
              </button>
              <span className="text-[11px] text-[#6B6B61] ml-auto">
                {data.rows.length} entries · {data.from_date} → {data.to_date}
              </span>
            </div>

            {/* Aggregated daily table — same clean 7-column view */}
            <AggregatedLedgerTable
              rows={data.rows}
              isAgency={true}
              agencyFallback={agencyName}
              sortOrder={sortOrder}
              currentBalance={data.totals?.balance ?? null}
            />
          </>
        )}
      </main>
    </div>
  );
}
